import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, CheckCircle2, Clock } from "lucide-react";
import { Header } from "@/components/Header";
import { getSummary, getSession } from "@/lib/api";
import { fmt } from "@/lib/calc";

export default function Summary() {
  const { code } = useParams();
  const [summary, setSummary] = useState(null);
  const [session, setSession] = useState(null);

  const load = useCallback(() => {
    Promise.all([getSummary(code), getSession(code)])
      .then(([sum, s]) => { setSummary(sum); setSession(s); })
      .catch(() => {});
  }, [code]);

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load]);

  if (!summary) {
    return <div className="min-h-screen bg-background flex items-center justify-center"><Loader2 className="animate-spin" /></div>;
  }

  const cur = summary.currency;

  return (
    <div className="min-h-screen bg-background pb-16">
      <Header subtitle="Sumar plăți" />
      <main className="max-w-md mx-auto px-4 sm:px-6 pt-8">
        <h1 className="font-display text-3xl font-black tracking-tight">{session?.restaurant_name || "Sumar"}</h1>
        <p className="text-muted-foreground mt-1">{session?.date}</p>

        <div className="mt-6 bg-card border border-border rounded-[28px] p-6 text-center shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
          <span className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Total notă</span>
          <p className="font-display text-5xl font-black tracking-tighter mt-2">{fmt(summary.computed_total, cur)}</p>
          <div className="flex justify-center gap-6 mt-4 text-sm text-muted-foreground">
            <span>Produse {fmt(summary.items_subtotal, cur)}</span>
            {summary.tip > 0 && <span>Bacșiș {fmt(summary.tip, cur)}</span>}
            {summary.discount > 0 && <span>− {fmt(summary.discount, cur)}</span>}
          </div>
        </div>

        <div className="mt-6 space-y-3">
          {summary.guests.map((g, i) => (
            <motion.div
              key={g.guest_id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              className="bg-card border border-border rounded-[20px] p-4 flex items-center gap-3"
            >
              <span className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center font-bold">
                {g.name.charAt(0).toUpperCase()}
              </span>
              <div className="flex-1">
                <p className="font-semibold">{g.name}</p>
                <p className={`text-xs flex items-center gap-1 ${g.paid ? "text-success" : "text-muted-foreground"}`}>
                  {g.paid ? <><CheckCircle2 size={12} /> Plătit</> : <><Clock size={12} /> În așteptare</>}
                </p>
              </div>
              <span className="font-display text-xl font-black tracking-tight">{fmt(g.total, cur)}</span>
            </motion.div>
          ))}
        </div>
      </main>
    </div>
  );
}
